import { openDB, DBSchema, IDBPDatabase } from 'idb'

interface OfflineSubmission {
  id: string
  client_id: string
  period_id: number
  indicator_id: number
  submission_date: string
  value: number
  notes?: string
  created_at: string
  synced: boolean
}

interface OfflineAttachment {
  id: string
  submission_client_id: string
  file: Blob
  filename: string
  file_type: string
  synced: boolean
}

interface KPIDBSchema extends DBSchema {
  submissions: {
    key: string
    value: OfflineSubmission
    indexes: {
      'by-synced': number
      'by-period': number
    }
  }
  attachments: {
    key: string
    value: OfflineAttachment
    indexes: {
      'by-submission': string
      'by-synced': number
    }
  }
  indicators: {
    key: number
    value: {
      id: number
      code: string
      name: string
      target: number
      points_per_unit: number
      max_points: number
      requires_attachment: boolean
      attachment_type?: string
    }
  }
  periods: {
    key: number
    value: {
      id: number
      year: number
      month: number
      name?: string
      start_date: string
      end_date: string
      is_locked: boolean
    }
  }
  sync_queue: {
    key: string
    value: {
      id: string
      operation: 'create' | 'update' | 'delete'
      entity_type: string
      client_id: string
      payload: object
      created_at: string
      retry_count: number
    }
  }
}

const DB_NAME = 'aeon-kpi-db'
const DB_VERSION = 1

let dbInstance: IDBPDatabase<KPIDBSchema> | null = null

export async function getDB(): Promise<IDBPDatabase<KPIDBSchema>> {
  if (dbInstance) return dbInstance

  dbInstance = await openDB<KPIDBSchema>(DB_NAME, DB_VERSION, {
    upgrade(db) {
      // Submissions store
      if (!db.objectStoreNames.contains('submissions')) {
        const submissionStore = db.createObjectStore('submissions', { keyPath: 'id' })
        submissionStore.createIndex('by-synced', 'synced')
        submissionStore.createIndex('by-period', 'period_id')
      }

      // Attachments store
      if (!db.objectStoreNames.contains('attachments')) {
        const attachmentStore = db.createObjectStore('attachments', { keyPath: 'id' })
        attachmentStore.createIndex('by-submission', 'submission_client_id')
        attachmentStore.createIndex('by-synced', 'synced')
      }

      // Indicators store (cache)
      if (!db.objectStoreNames.contains('indicators')) {
        db.createObjectStore('indicators', { keyPath: 'id' })
      }

      // Periods store (cache)
      if (!db.objectStoreNames.contains('periods')) {
        db.createObjectStore('periods', { keyPath: 'id' })
      }

      // Sync queue
      if (!db.objectStoreNames.contains('sync_queue')) {
        db.createObjectStore('sync_queue', { keyPath: 'id' })
      }
    },
  })

  return dbInstance
}

// Submission operations
export async function saveOfflineSubmission(submission: Omit<OfflineSubmission, 'id' | 'synced'>): Promise<string> {
  const db = await getDB()
  const id = `sub_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`
  const fullSubmission: OfflineSubmission = {
    ...submission,
    id,
    client_id: id,
    synced: false,
  }
  await db.put('submissions', fullSubmission)

  // Add to sync queue
  await addToSyncQueue('create', 'submission', id, submission)

  return id
}

export async function getOfflineSubmissions(periodId?: number): Promise<OfflineSubmission[]> {
  const db = await getDB()
  if (periodId) {
    return db.getAllFromIndex('submissions', 'by-period', periodId)
  }
  return db.getAll('submissions')
}

export async function getUnsyncedSubmissions(): Promise<OfflineSubmission[]> {
  const db = await getDB()
  return db.getAllFromIndex('submissions', 'by-synced', 0) // false = 0 in IndexedDB
}

export async function markSubmissionSynced(id: string): Promise<void> {
  const db = await getDB()
  const submission = await db.get('submissions', id)
  if (submission) {
    submission.synced = true
    await db.put('submissions', submission)
  }
}

// Attachment operations
export async function saveOfflineAttachment(
  submissionClientId: string,
  file: Blob,
  filename: string,
  fileType: string
): Promise<string> {
  const db = await getDB()
  const id = `att_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`
  await db.put('attachments', {
    id,
    submission_client_id: submissionClientId,
    file,
    filename,
    file_type: fileType,
    synced: false,
  })
  return id
}

export async function getAttachmentsForSubmission(submissionClientId: string): Promise<OfflineAttachment[]> {
  const db = await getDB()
  return db.getAllFromIndex('attachments', 'by-submission', submissionClientId)
}

// Sync queue operations
export async function addToSyncQueue(
  operation: 'create' | 'update' | 'delete',
  entityType: string,
  clientId: string,
  payload: object
): Promise<void> {
  const db = await getDB()
  const id = `sync_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`
  await db.put('sync_queue', {
    id,
    operation,
    entity_type: entityType,
    client_id: clientId,
    payload,
    created_at: new Date().toISOString(),
    retry_count: 0,
  })
}

export async function getSyncQueue(): Promise<Array<{
  id: string
  operation: 'create' | 'update' | 'delete'
  entity_type: string
  client_id: string
  payload: object
  created_at: string
  retry_count: number
}>> {
  const db = await getDB()
  return db.getAll('sync_queue')
}

export async function removeSyncQueueItem(id: string): Promise<void> {
  const db = await getDB()
  await db.delete('sync_queue', id)
}

// Cache operations
export async function cacheIndicators(indicators: Array<{
  id: number
  code: string
  name: string
  target: number
  points_per_unit: number
  max_points: number
  requires_attachment: boolean
  attachment_type?: string
}>): Promise<void> {
  const db = await getDB()
  const tx = db.transaction('indicators', 'readwrite')
  await Promise.all([
    ...indicators.map((ind) => tx.store.put(ind)),
    tx.done,
  ])
}

export async function getCachedIndicators(): Promise<Array<{
  id: number
  code: string
  name: string
  target: number
  points_per_unit: number
  max_points: number
  requires_attachment: boolean
  attachment_type?: string
}>> {
  const db = await getDB()
  return db.getAll('indicators')
}

export async function cachePeriods(periods: Array<{
  id: number
  year: number
  month: number
  name?: string
  start_date: string
  end_date: string
  is_locked: boolean
}>): Promise<void> {
  const db = await getDB()
  const tx = db.transaction('periods', 'readwrite')
  await Promise.all([
    ...periods.map((period) => tx.store.put(period)),
    tx.done,
  ])
}

export async function getCachedPeriods(): Promise<Array<{
  id: number
  year: number
  month: number
  name?: string
  start_date: string
  end_date: string
  is_locked: boolean
}>> {
  const db = await getDB()
  return db.getAll('periods')
}

// Clear all offline data
export async function clearOfflineData(): Promise<void> {
  const db = await getDB()
  await Promise.all([
    db.clear('submissions'),
    db.clear('attachments'),
    db.clear('sync_queue'),
  ])
}

// Clear PWA caches for period-sensitive data (scores, submissions)
export async function clearPeriodCaches(): Promise<void> {
  if ('caches' in window) {
    try {
      // Delete caches that contain period-sensitive data
      const cacheNames = ['scores-cache', 'submissions-cache', 'api-cache']
      await Promise.all(
        cacheNames.map(async (name) => {
          const cache = await caches.open(name)
          const keys = await cache.keys()
          await Promise.all(keys.map((key) => cache.delete(key)))
        })
      )
      console.log('Period caches cleared successfully')
    } catch (error) {
      console.error('Failed to clear period caches:', error)
    }
  }
}

// Check if period has changed and clear caches if needed
export async function checkAndClearPeriodCache(currentPeriodId: number): Promise<boolean> {
  const PERIOD_KEY = 'aeon_last_period_id'
  const lastPeriodId = localStorage.getItem(PERIOD_KEY)

  if (lastPeriodId && parseInt(lastPeriodId, 10) !== currentPeriodId) {
    // Period has changed, clear caches
    await clearPeriodCaches()
    localStorage.setItem(PERIOD_KEY, String(currentPeriodId))
    return true // Cache was cleared
  }

  if (!lastPeriodId) {
    localStorage.setItem(PERIOD_KEY, String(currentPeriodId))
  }

  return false
}
